import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import AddTeamMemberForm from "~/components/forms/AddTeamMemberForm";
import { NOOP_FALSE } from "~/constants/callbacks";
import { BLANK_OPTION } from "~/constants/dropdowns/BlankOption";
import { CONTACT_OWNER_OPTION } from "~/constants/dropdowns/ContactOwnerOption";
import { apiFetch } from "~/lib/fetchClient";
import Select from "./SelectWrapper/Select";

const EMPTY_ADDITIONAL_OPTIONS = [];
export const NO_OWNER_OPTION = { value: "__no_owner__", label: "No owner" };
export const NO_OWNER_OPTIONS = [NO_OWNER_OPTION];

export const contactOwnerValue = (option) =>
  option?.value === NO_OWNER_OPTION.value ? null : (option?.value ?? null);

export const contactOwnerSelectValue = (value) =>
  value === null || value === "" || value === undefined
    ? NO_OWNER_OPTION.value
    : value;

const TeamMemberSelect = ({
  id = "",
  name = "",
  value = "",
  placeholder = "",
  apiResource = "/team/members_options",
  onChange = NOOP_FALSE,
  isMulti = false,
  isClearable = false,
  isInvalid = false,
  isDisabled = false,
  isSearchable,
  addBlank = false,
  contactOwner = false,
  showAll = false,
  clearLabel = "Show All",
  level = null,
  skipAvatar = false,
  additionalOptions = EMPTY_ADDITIONAL_OPTIONS,
  addable = null,
  label = "",
  style,
  ...restProps
}) => {
  const isMounted = useRef(true);
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [validateOptions, setValidateOptions] = useState(false);
  const [addableOpen, setAddableOpen] = useState(false);

  const addStaticOptions = useCallback(
    (sourceOptions = []) => {
      const nextOptions = [...sourceOptions];

      if (addBlank) {
        nextOptions.unshift(BLANK_OPTION);
      }
      if (contactOwner) {
        nextOptions.unshift(CONTACT_OWNER_OPTION);
      }
      if (additionalOptions.length > 0) {
        nextOptions.unshift(...additionalOptions);
      }
      if (!isMulti && showAll) {
        nextOptions.unshift({ value: "", label: clearLabel });
      }

      return nextOptions;
    },
    [addBlank, contactOwner, isMulti, showAll, clearLabel, additionalOptions],
  );

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetchOptions = useCallback(async () => {
    setIsLoading(true);
    try {
      // Parse existing query params from apiResource (e.g. "?only_existing=true")
      const [path, queryString] = apiResource.split("?");
      const existingParams = Object.fromEntries(
        new URLSearchParams(queryString || ""),
      );
      const params = { ...existingParams, ...(level ? { level } : {}) };

      const body = await apiFetch.get(path, { params });

      if (isMounted.current) {
        const data = body.data ? body.data : body;
        const fetchedOptions = data.map((u) => ({
          ...(!skipAvatar && { user: u }),
          value: u.id.toString(),
          label: u.name,
        }));

        setOptions(addStaticOptions(fetchedOptions));
        setValidateOptions(true);
      }
    } catch {
      if (isMounted.current) {
        setOptions(addStaticOptions());
        setValidateOptions(false);
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  }, [apiResource, level, skipAvatar, addStaticOptions]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  const handleAddableSave = useCallback(
    async (newMember) => {
      setAddableOpen(false);
      await fetchOptions();
      if (onChange) {
        const mergedValue =
          isMulti && value
            ? [...(Array.isArray(value) ? value : [value]), newMember]
            : newMember;
        onChange(mergedValue);
      }
    },
    [fetchOptions, onChange, isMulti, value],
  );

  if (addableOpen && addable) {
    return (
      <AddTeamMemberForm
        title={addable.title || "Add Team Member"}
        onSave={handleAddableSave}
        onCancel={() => setAddableOpen(false)}
      />
    );
  }

  return (
    <div style={style}>
      {(label || addable) && (
        <label htmlFor={id} className="d-block">
          {addable && (
            <a
              href="#add"
              className="pull-right"
              onClick={(e) => {
                e.preventDefault();
                setAddableOpen(true);
              }}
            >
              {addable.title || "Add Team Member"}
            </a>
          )}
          {label}
        </label>
      )}
      <Select
        validateValue={validateOptions}
        id={id}
        name={name}
        options={options}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        isMulti={isMulti}
        isClearable={isClearable}
        isInvalid={isInvalid}
        isDisabled={isDisabled}
        isSearchable={isSearchable}
        isLoading={isLoading}
        {...restProps}
      />
    </div>
  );
};

TeamMemberSelect.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  apiResource: PropTypes.string,
  onChange: PropTypes.func,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
  ]),
  isMulti: PropTypes.bool,
  isClearable: PropTypes.bool,
  isInvalid: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isSearchable: PropTypes.bool,
  addBlank: PropTypes.bool,
  contactOwner: PropTypes.bool,
  showAll: PropTypes.bool,
  clearLabel: PropTypes.string,
  level: PropTypes.string,
  skipAvatar: PropTypes.bool,
  additionalOptions: PropTypes.arrayOf(PropTypes.object),
  addable: PropTypes.shape({
    title: PropTypes.string,
  }),
  label: PropTypes.string,
  style: PropTypes.shape({}),
};

const createVariant = (defaultApiResource) => {
  const Variant = (props) => (
    <TeamMemberSelect apiResource={defaultApiResource} {...props} />
  );
  Variant.propTypes = TeamMemberSelect.propTypes;
  return Variant;
};

export const TeammateSelect = createVariant("/team/teammates_options");
export const OwnerSelect = createVariant("/team/owners_options");
export const ReferrerSelect = createVariant("/team/referrers_options");
export const TeamAffiliateSelect = createVariant("/team/affiliates_options");
export const ConsolidatedUsersSelect = createVariant("/videos/users_options");

// Aliases for backward compatibility (prefer *Select names)
export const TeammatePicker = TeammateSelect;
export const OwnerPicker = OwnerSelect;
export const ReferrerPicker = ReferrerSelect;
export const TeamAffiliatePicker = TeamAffiliateSelect;
export const ConsolidatedUsersWithInternalRolePicker = ConsolidatedUsersSelect;
export const TeamMemberPicker = TeamMemberSelect;

export default TeamMemberSelect;
